/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { isBrowser } from '@utils/environmentCheck';

import { NAVBAR_HEIGHT_PX } from '@common/constants/ui';
import useIsomorphicLayoutEffect from '@common/hooks/useIsomorphicLayoutEffect';

const NAVBAR_HEIGHT_DOUBLED_PX = NAVBAR_HEIGHT_PX * 2;

export const useStickyElement = (stickyElementRef, defaultOffset = 0) => {
  const topOffsetRef = useRef(NAVBAR_HEIGHT_PX);
  const previousScrollPos = useRef(isBrowser ? window.scrollY : 0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null);
  const router = useRouter();

  const setRefValues = useCallback(
    (topOffset, windowScrollYPosition = 0) => {
      if (!stickyElementRef?.current || !isBrowser) return;
      const finalOffset = topOffset + defaultOffset;

      topOffsetRef.current = finalOffset;
      stickyElementRef.current.style.top = `${finalOffset}px`;
      previousScrollPos.current = windowScrollYPosition;
    },
    [defaultOffset, stickyElementRef],
  );

  useEffect(() => {
    setShouldAnimate(false);
    setRefValues(NAVBAR_HEIGHT_PX, window.scrollY);
  }, [router.query.id]);

  const handleScroll = useCallback(() => {
    if (!isBrowser || !stickyElementRef?.current) return;
    const { scrollY: windowScrollY, innerHeight: windowInnerHeight } = window;
    const { offsetHeight: stickyElementOffsetHeight } =
      stickyElementRef?.current || {};
    const { top: stickyElementCurrentTop } =
      stickyElementRef?.current?.getBoundingClientRect() || {};
    const previousScrollPosCurrent = previousScrollPos.current;
    const isScrollingUp = windowScrollY < previousScrollPosCurrent;
    const endScroll = windowInnerHeight - stickyElementOffsetHeight;

    setScrollDirection(isScrollingUp ? 'up' : 'down');
    let topOffsetCurrent = topOffsetRef.current;

    if (windowInnerHeight > stickyElementOffsetHeight) {
      setShouldAnimate(true);
      const isStickyElement =
        (!isScrollingUp &&
          stickyElementCurrentTop === NAVBAR_HEIGHT_PX + defaultOffset) ||
        (isScrollingUp &&
          stickyElementCurrentTop === NAVBAR_HEIGHT_DOUBLED_PX + defaultOffset);

      setIsSticky(isStickyElement);

      setRefValues(
        !isScrollingUp ? NAVBAR_HEIGHT_PX : NAVBAR_HEIGHT_DOUBLED_PX,
        windowScrollY,
      );

      return;
    }

    if (isScrollingUp) {
      topOffsetCurrent =
        NAVBAR_HEIGHT_DOUBLED_PX > topOffsetCurrent
          ? topOffsetCurrent + previousScrollPosCurrent - windowScrollY
          : NAVBAR_HEIGHT_DOUBLED_PX;
    } else {
      topOffsetCurrent =
        topOffsetCurrent > endScroll
          ? topOffsetCurrent + previousScrollPosCurrent - windowScrollY
          : endScroll;
    }

    setRefValues(topOffsetCurrent, windowScrollY);
  }, [defaultOffset, setRefValues, stickyElementRef]);

  useIsomorphicLayoutEffect(() => {
    if (!isBrowser || !stickyElementRef?.current) return undefined;

    handleScroll();

    window.addEventListener('scroll', handleScroll, {
      capture: false,
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, stickyElementRef]);

  return { shouldAnimate, isSticky, scrollDirection };
};
